import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { Box, Button, Step, StepLabel, Typography } from '@mui/material'
import clsx from 'clsx'
import { parse, isTomorrow } from 'date-fns'

import Divider from '../../../ui/components/divider'

import { getWeekDay } from '../../../libs/date'
import useTranslations from '../../../i18n/useTranslations'
import {
  INCIDENTS_CODES_ALLOWED_FOR_RESOLUTION,
  SHIPPING_STATUS_CODES,
  SHIPPING_WITH_ERE,
} from '../../../common/constants'
import { addressPropTypes } from '../../../common/propTypes'

import Address from '../../components/Address'

import useStyles from './styles'
import { ICONS, CURRENT, DISABLED, PAST, ERROR, CANCELLED } from './constants'

const InfoWrapperIsShippingDetail = ({ children }) => {
  const classes = useStyles()
  return <Box className={classes.isShippingDetailInfoWrapper}>{children}</Box>
}

InfoWrapperIsShippingDetail.propTypes = {
  children: PropTypes.node,
}

const DateDetail = ({ label, date }) => {
  const t = useTranslations()
  const classes = useStyles()

  let weekDay = ''
  let showTomorrowLabel = false

  if (Boolean(date)) {
    const parsedDate = parse(date, 'dd/MM/yyyy', new Date())
    showTomorrowLabel = isTomorrow(parsedDate)
    weekDay = getWeekDay(parsedDate)
  }

  return (
    <Box className={classes.dateWrapper}>
      <Typography variant="body2" className={classes.shippingDateLabel}>
        {label}
      </Typography>
      <Typography variant="body2" className={classes.shippingDate}>
        {Boolean(date) ? (
          <>
            {showTomorrowLabel ? t('noun:tomorrow') + ', ' : null}
            {weekDay} {date}
          </>
        ) : (
          t('noun:not-defined')
        )}
      </Typography>
    </Box>
  )
}

DateDetail.propTypes = {
  label: PropTypes.string,
  date: PropTypes.string,
}

const TrackingLineStep = ({
  code,
  label,
  description,
  type,
  nextStepType,
  button,
  canBeReturnedButton,
  isShippingDetail,
  shippingInfo,
  shippingDates,
  addresses,
  showSeeReturn,
  hasPudoStatus,
  isResolved,
  isCancelled,
  shippingStatusCode,
}) => {
  const classes = useStyles()
  const t = useTranslations()
  const isSolvable = [
    'noun:resolve-incident',
    'action:schedule-pickup',
  ].includes(button?.labelKey)
  const isUnscheduled = ['action:schedule-pickup'].includes(button?.labelKey)

  const formatName = name => {
    let nameArray = name.split(' ')
    if (isUnscheduled) return name
    if (type === 'error' && name !== '-' && !returned) {
      switch (nameArray[nameArray.length - 1].toLowerCase()) {
        case 'recogido':
          return `Incidencia en la recogida`
        case 'entregado':
          return `Incidencia en la entrega`
        case 'devuelto':
          return `Incidencia en la devolución`
        default:
          return `Incidencia en el ${nameArray[
            nameArray.length - 1
          ].toLowerCase()}`
      }
    }
    return name
  }
  const returned =
    code === SHIPPING_STATUS_CODES.returned &&
    (shippingInfo?.relatedReturn.returnShippingCode ||
      shippingInfo?.relatedReturn.reverseReturnShippingCode)
  const InfoWrapper = isShippingDetail ? InfoWrapperIsShippingDetail : Fragment
  const isOriginPudo = shippingInfo.services.some(
    service => service.additional_code === SHIPPING_WITH_ERE,
  )

  return (
    <Step
      className={clsx(
        isUnscheduled
          ? classes['unscheduledState']
          : isResolved || returned
          ? classes['resolvedState']
          : classes[type],
        nextStepType ? `next-status_${returned ? 'past' : nextStepType}` : null,
      )}
    >
      {(isShippingDetail && code === SHIPPING_STATUS_CODES.manifested) ||
      (isShippingDetail && code === SHIPPING_STATUS_CODES.cancelled) ? (
        <DateDetail
          label={t('noun:date-first-reading')}
          date={shippingDates.recordDate}
        />
      ) : isShippingDetail && code === SHIPPING_STATUS_CODES.delivered ? (
        <DateDetail
          label={t('noun:expected-delivery-date')}
          date={shippingDates.deliveryDate}
        />
      ) : null}

      <StepLabel className={clsx(isShippingDetail && classes.isShippingDetail)}>
        <Box className={classes.iconWrapper}>
          {isUnscheduled
            ? ICONS['unscheduled']
            : isResolved && !returned
            ? ICONS['resolved']
            : isSolvable && !returned
            ? ICONS['solvable']
            : returned
            ? ICONS['past']
            : ICONS[type]}
        </Box>
        <InfoWrapper>
          <Typography component="span" variant="h4" className={classes.label}>
            {formatName(label)}
          </Typography>
          {description &&
          !returned &&
          !isCancelled &&
          shippingStatusCode !== SHIPPING_STATUS_CODES.delivered ? (
            <Typography
              component="span"
              variant="subtitle1"
              className={
                isResolved ? classes.descriptionSolved : classes.description
              }
            >
              {description}
            </Typography>
          ) : null}
          {isUnscheduled ? (
            <Typography
              component="span"
              variant="subtitle1"
              className={classes.descriptionUnscheduled}
            >
              {'Sin programar'}
            </Typography>
          ) : null}
          {button && !returned ? (
            <Box>
              {!shippingInfo.isCancelled && (
                <Button
                  size="small"
                  onClick={button.onClick}
                  className={classes.button}
                  disabled={isResolved}
                >
                  {t(button.labelKey)}
                </Button>
              )}
              {isResolved ? (
                <Typography
                  component="span"
                  variant="subtitle1"
                  className={classes.resolved}
                >
                  {t('shipping-tracking:already-resolved')}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {code === SHIPPING_STATUS_CODES.collected ? (
            <Box className={classes.addressWrapper}>
              <Divider variant="middle" />
              {isOriginPudo ? (
                <Typography
                  component="span"
                  variant="title"
                  className={classes.message}
                >
                  {t('shipping-detail:ctt-express-point')}
                </Typography>
              ) : (
                Boolean(addresses?.from) && (
                  <Address
                    data={addresses.from}
                    className={classes.address}
                    isShippingDetailDesktop
                  />
                )
              )}
            </Box>
          ) : code === SHIPPING_STATUS_CODES.pudo &&
            hasPudoStatus &&
            Boolean(addresses?.to) ? (
            <Box className={classes.addressWrapper}>
              <Divider variant="middle" />
              <Address
                data={addresses.to}
                className={classes.address}
                isShippingDetailDesktop
              />
            </Box>
          ) : code === SHIPPING_STATUS_CODES.delivered &&
            !hasPudoStatus &&
            Boolean(addresses?.to) ? (
            <Box className={classes.addressWrapper}>
              <Divider variant="middle" />
              <Address
                data={addresses.to}
                className={classes.address}
                isShippingDetailDesktop
              />
            </Box>
          ) : returned ? (
            <Link
              to={`/shippings-tracking/${
                shippingInfo?.relatedReturn.returnShippingCode ||
                shippingInfo?.relatedReturn.reverseReturnShippingCode
              }`}
            >
              {showSeeReturn ? (
                <Button size="small" className={classes.button}>
                  {t('shipping-detail:see-return')}
                </Button>
              ) : null}
            </Link>
          ) : null}
        </InfoWrapper>
      </StepLabel>
    </Step>
  )
}

TrackingLineStep.propTypes = {
  code: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  showSeeReturn: PropTypes.bool,
  description: PropTypes.string,
  type: PropTypes.oneOf([CURRENT, DISABLED, PAST, ERROR, CANCELLED]),
  nextStepType: PropTypes.oneOf([CURRENT, DISABLED, PAST, ERROR, CANCELLED]),
  button: PropTypes.shape({
    labelKey: PropTypes.string,
    onClick: PropTypes.func,
  }),
  canBeReturnedButton: PropTypes.shape({
    labelKey: PropTypes.string,
    onClick: PropTypes.func,
  }),
  isShippingDetail: PropTypes.bool,
  shippingInfo: PropTypes.shape({
    relatedReturn: PropTypes.shape({
      returnShippingCode: PropTypes.string,
      reverseReturnShippingCode: PropTypes.string,
    }),
  }),
  shippingDates: PropTypes.shape({
    recordDate: PropTypes.string,
    deliveryDate: PropTypes.string,
  }),
  addresses: PropTypes.shape({
    from: addressPropTypes,
    to: addressPropTypes,
  }),
  hasPudoStatus: PropTypes.bool,
  isResolved: PropTypes.bool,
  isCancelled: PropTypes.bool,
  shippingStatusCode: PropTypes.string,
}

export default TrackingLineStep
